import { getAccessToken } from '../auth/storage.js';
import { handleExpiredSession } from '../auth/session.js';
import { restoreScroll } from '../router.js';
import { getTopicHead, loadTopic } from '../study/content.js';
import { isTopicDone, markTopicDone } from '../study/completion.js';
import { isFavorite as isTopicFavorite, toggleFavorite } from '../study/favorites.js';
import { backButton } from '../components/branding.js';
import { escapeHtml, hashHref } from '../utils/html.js';
import { showBlobImage } from '../utils/blobImage.js';
import { errorCard, loaderView, wireRetry } from '../components/feedback.js';
import { openPhrasePreview } from '../components/phrasePreview.js';
import { phraseRow } from '../components/phraseRow.js';
import { playButtonHtml } from '../components/playButton.js';
import { ICONS } from '../components/icons.js';
import { renderAppScreen } from '../components/shell.js';
import { togglePillHtml, wireTogglePill } from '../components/togglePill.js';
import { createPlayBar, playBarHtml } from '../components/playBar.js';

const FAVORITE_LABELS = ['Dodaj do ulubionych', 'Usuń z ulubionych'];
const FAVORITE_ICONS = [ICONS.heart('h-5 w-5'), ICONS.heartFilled('h-5 w-5')];

/* -------------------------------------------------------------------------- */
/* Markup                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * "Ukończono" badge — shown when this topic has a row in `topics_done`, and
 * revealed in place the moment a level's dialogue plays through to its end.
 */
function doneBadgeHtml(done) {
  return `
    <span id="topic-done" ${done ? '' : 'hidden'} role="status"
      class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-2 text-sm font-semibold text-green-700">
      ${ICONS.check('h-4 w-4')}
      Ukończono
    </span>`;
}

/**
 * Card header: the cached image + title + intro (from the head row), then the
 * favorite toggle and the completion badge. Both are rendered only when we have a
 * head row: the favorites list reads everything it shows from `topics_head`, so a
 * topic without one could not be listed — and a check on a screen that has no
 * title yet would be the only thing on it.
 *
 * `done` is passed in rather than read off the row — completion is its own store
 * now (`study/completion.js`).
 */
function headerCardHtml(head, done) {
  const title = head?.title ? escapeHtml(head.title) : 'Temat';
  const image =
    head?.imageBlob instanceof Blob
      ? `<img data-topic-image alt="" class="mb-4 aspect-video w-full rounded-2xl bg-mist object-cover" />`
      : '';
  const intro = head?.intro
    ? `<h3 class="mt-2 text-sm font-normal leading-snug text-neutral-600">${escapeHtml(head.intro)}</h3>`
    : '';
  const actions = head
    ? `<div class="mt-4 flex flex-wrap items-center gap-2">
         ${togglePillHtml({
           id: 'topic-favorite',
           offLabel: FAVORITE_LABELS[0],
           offIcon: FAVORITE_ICONS[0],
         })}
         ${doneBadgeHtml(done)}
       </div>`
    : '';

  return `
    ${image}
    <h1 class="text-2xl font-semibold tracking-tight text-neutral-900">${title}</h1>
    ${intro}
    ${actions}`;
}

/**
 * Bind the header's favorite toggle to the `favorites` store — the shared pill,
 * so it behaves exactly like the phrase preview's dictionary toggle.
 */
function wireFavorite(headerEl, topicId) {
  return wireTogglePill(headerEl, {
    read: () => isTopicFavorite(topicId),
    write: () => toggleFavorite(topicId),
    labels: FAVORITE_LABELS,
    icons: FAVORITE_ICONS,
    onClass: 'text-red-500',
  });
}

/**
 * Point the top bar's back link at this topic's own chapter, learned from the
 * head row (the detail payload carries no chapter). It is only ever followed on
 * a cold start straight into `#/topic/:id`; normally the button pops the history
 * entry we came from, which is the list the user actually opened this from.
 */
function wireBackFallback(main, head) {
  const link = main.parentElement?.querySelector('[data-back]');
  if (!link || !head?.chapterId) return;
  link.setAttribute('href', hashHref('/chapter/{}', head.chapterId));
}

/**
 * Level switchers for the bottom bar — a pill per level in `[from, to)`, ordered
 * by `position`, carrying the same `data-tab` index the bar's click handler
 * reads. Rendered into the bar's left and right slots so the usual two-level
 * topic (A1/A2 + B1/B2) reads as "level — play — level".
 */
function levelButtonsHtml(levels, active, from, to) {
  return levels
    .slice(from, to)
    .map((level, i) => {
      const index = from + i;
      const on = index === active;
      return `
        <button type="button" data-tab="${index}" aria-pressed="${on}"
          class="rounded-full px-3.5 py-2 text-sm font-semibold transition active:scale-95 ${
            on ? 'bg-brand text-white' : 'bg-lilac/50 text-neutral-600 hover:bg-lilac'
          }">
          ${escapeHtml(level.name || `Poziom ${index + 1}`)}
        </button>`;
    })
    .join('');
}

/**
 * One sentence line: a play control, the accordion trigger, and its panel.
 *
 * The translation panel carries **no fill and no radius** — square, indented, and
 * marked by a single `border-l-2` rule in the accent. Exercises use exactly the
 * same treatment, so every revealed translation on this screen looks alike
 * whichever section it is in (their cards already tell the sections apart).
 */
function sentenceHtml(sentence, audioBlob) {
  const id = sentence?.id ?? '';
  const hasAudio = audioBlob && audioBlob[id] instanceof Blob;
  const text = escapeHtml(sentence?.sentence ?? '');
  const tense = sentence?.tense
    ? `<p class="mt-1 text-xs text-neutral-600">${escapeHtml(sentence.tense)}</p>`
    : '';

  return `
    <li data-sentence="${escapeHtml(id)}" class="rounded-xl px-2 py-2 transition">
      <div class="flex items-start gap-1.5">
        ${playButtonHtml({
          hook: `data-play-sentence="${escapeHtml(id)}"`,
          label: 'Odtwórz zdanie',
          hasAudio,
          className: '-mt-0.5',
        })}
        <button type="button" data-sentence-toggle="${escapeHtml(id)}" aria-expanded="false"
          class="flex-1 text-left font-medium text-[0.95rem] leading-relaxed text-neutral-800">
          ${text}
        </button>
      </div>
      <div data-sentence-panel="${escapeHtml(id)}" hidden class="ml-9 mt-1 border-l-2 border-brand/40 px-3 py-2">
        <p class="text-sm text-neutral-700">${escapeHtml(sentence?.translation ?? '')}</p>
        ${tense}
      </div>
    </li>`;
}

/** One dialogue block: the speaker (bold) and their sentences. */
function dialogueHtml(dialogue, audioBlob) {
  const sentences = Array.isArray(dialogue?.sentences) ? dialogue.sentences : [];
  return `
    <div>
      <p class="mb-1.5 text-sm font-bold text-neutral-900">${escapeHtml(dialogue?.person ?? '')}</p>
      <ul class="flex flex-col gap-1">
        ${sentences.map((sentence) => sentenceHtml(sentence, audioBlob)).join('')}
      </ul>
    </div>`;
}

/**
 * "Przydatne zwroty" — the level's phrases listed beneath the dialogue, as the
 * shared `phraseRow`.
 *
 * `level.phrases` is a map of `phraseId → phrase object` (not of strings), so the
 * values are what gets rendered. Their clips don't live in the topic's
 * `audio_blob` map but in the `phrases` store, hence the separate `phraseAudio`.
 */
function phrasesHtml(phrases, phraseAudio) {
  const list = Object.values(phrases && typeof phrases === 'object' ? phrases : {}).filter(Boolean);
  if (!list.length) return '';
  // Light surface, so the block reads as its own section next to the dialogue
  // (the same cream fill the chapter/topic list rows sit on).
  return `
    <div class="rounded-2xl bg-cream p-4">
      <h2 class="mb-2 text-lg font-semibold text-neutral-900">Przydatne zwroty</h2>
      <ul class="flex flex-col gap-1">
        ${list.map((phrase) => phraseRow(phrase, phraseAudio?.[phrase.id])).join('')}
      </ul>
    </div>`;
}

/**
 * One exercise line: the Polish prompt as the accordion trigger, its English
 * answer in the panel — the mirror of {@link sentenceHtml}, which shows the
 * English and hides the Polish.
 *
 * Deliberately audio-free: the topic's `audio` map covers dialogue sentences only
 * and an exercise carries no clip of its own, so the row has no play control. The
 * chevron sits where that button would be — leading the line, like the sentence
 * and phrase rows — and turns a quarter clockwise while the answer is open.
 */
function exerciseHtml(exercise) {
  const id = escapeHtml(exercise?.id ?? '');
  return `
    <li data-exercise="${id}" class="rounded-xl px-2 py-2 transition">
      <button type="button" data-exercise-toggle="${id}" aria-expanded="false"
        class="flex w-full items-start gap-2 text-left">
        <span data-exercise-chevron class="mt-1 shrink-0 text-brand/50 transition-transform">
          ${ICONS.chevronRight('h-4 w-4')}
        </span>
        <p class="min-w-0 flex-1 font-medium text-[0.95rem] leading-relaxed text-neutral-800">
          ${escapeHtml(exercise?.content ?? '')}
        </p>
      </button>
      <div data-exercise-panel="${id}" hidden class="ml-6 mt-1 border-l-2 border-brand/40 px-3 py-2">
        <p class="text-sm text-neutral-700">${escapeHtml(exercise?.translation ?? '')}</p>
      </div>
    </li>`;
}

/**
 * "Ćwiczenia" — the level's translation drills, in their own card beneath
 * "Przydatne zwroty". Same card shape as the phrases block but a **mist** tint
 * instead of its cream one, so the two sections don't read as one long list; the
 * blue is otherwise unused on this screen, which keeps it clear of the meanings
 * the rest of the palette carries (brand = playing, green = done, amber = saved).
 * The answer panels are the same unfilled, accent-ruled blocks the sentences use —
 * the card tint is what separates the sections, not the panels inside them.
 *
 * `level.exercises` is an **array** (unlike `phrases`, which is a map), and the
 * payload gives its items no `position`, so the response order is the order we
 * render.
 */
function exercisesHtml(exercises) {
  const list = (Array.isArray(exercises) ? exercises : []).filter(Boolean);
  if (!list.length) return '';
  return `
    <div class="rounded-2xl bg-mist/40 p-4">
      <h2 class="mb-2 text-lg font-semibold text-neutral-900">Ćwiczenia</h2>
      <ul class="flex flex-col gap-1">
        ${list.map((exercise) => exerciseHtml(exercise)).join('')}
      </ul>
    </div>`;
}

/**
 * The full content for one level: its dialogues, then its phrases, then its
 * exercises.
 */
function levelHtml(level, audioBlob, phraseAudio) {
  // `dialogues` arrive already sorted — see `loadTopicView`.
  return `
    <div class="flex flex-col gap-6">
      ${(level?.dialogues || []).map((dialogue) => dialogueHtml(dialogue, audioBlob)).join('')}
      ${phrasesHtml(level?.phrases, phraseAudio)}
      ${exercisesHtml(level?.exercises)}
    </div>`;
}

/* -------------------------------------------------------------------------- */
/* Screen                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Resolve the topic (from IndexedDB, fetching + caching its audio on a cold or
 * stale cache), render the header, the bottom bar's level pills and the active
 * level, then wire the accordions, per-sentence controls and the shared Player.
 */
async function loadTopicView(main, topicId) {
  const headerEl = main.querySelector('#topic-header');
  const levelEl = main.querySelector('#topic-level');
  const levelStartEl = main.querySelector('#topic-levels-start');
  const levelEndEl = main.querySelector('#topic-levels-end');

  levelEl.innerHTML = loaderView('Pobieranie lekcji...', { fullscreen: false });

  let head = null;
  let topic = null;
  let phraseAudio = {};
  let error = null;
  try {
    ({ head, topic, phraseAudio } = await loadTopic(getAccessToken(), topicId));
  } catch (err) {
    if (handleExpiredSession(err)) return;
    error = err;
    try {
      head = await getTopicHead(topicId);
    } catch {
      /* IndexedDB unavailable — leave the header generic */
    }
  }

  // Completion is local-only and in its own store, so it is read separately —
  // and, like the favorite pill below, it still shows when the detail fetch failed.
  let done = false;
  try {
    done = await isTopicDone(topicId);
  } catch {
    /* IndexedDB unavailable — the badge stays hidden */
  }

  headerEl.innerHTML = headerCardHtml(head, done);
  showBlobImage(headerEl.querySelector('img[data-topic-image]'), head?.imageBlob);
  wireBackFallback(main, head);
  // Favorites are local-only, so the toggle works even when the detail fetch
  // failed — wire it before the error branch bails out.
  wireFavorite(headerEl, topicId);

  if (error || !topic) {
    levelEl.innerHTML = errorCard(error, 'Nie udało się wczytać tematu.');
    wireRetry(levelEl, () => loadTopicView(main, topicId));
    return;
  }

  // Sorted once, here: both the markup and the playlist read this array, and two
  // separate sorts are two chances for the highlight to drift from what plays.
  const levels = [...(topic.levels || [])]
    .sort((a, b) => a.position - b.position)
    .map((level) => ({
      ...level,
      dialogues: [...(level?.dialogues || [])].sort((a, b) => a.position - b.position),
    }));
  const audioBlob = topic.audio_blob || {};
  let active = 0;

  // The bar's level pills: first half left of the play button, rest to its
  // right. A single-level topic needs no switcher at all.
  const renderLevelButtons = () => {
    const multi = levels.length > 1;
    const split = Math.ceil(levels.length / 2);
    levelStartEl.innerHTML = multi ? levelButtonsHtml(levels, active, 0, split) : '';
    levelEndEl.innerHTML = multi ? levelButtonsHtml(levels, active, split, levels.length) : '';
  };

  // The active level's audio, in dialogue → sentence order. Handed to the play bar
  // as its playlist; it drops the sentences we have no clip for and slices the rest
  // from the resume point.
  const buildPlaylist = () => {
    const items = [];
    (levels[active]?.dialogues || []).forEach((dialogue) => {
      (dialogue.sentences || []).forEach((sentence) => {
        items.push({ id: sentence.id, blob: audioBlob[sentence.id] });
      });
    });
    return items;
  };

  const renderActive = () => {
    renderLevelButtons();
    levelEl.innerHTML = levels.length
      ? levelHtml(levels[active], audioBlob, phraseAudio)
      : `<p class="py-10 text-center text-sm font-medium text-neutral-600">Brak treści</p>`;
  };

  renderActive();
  restoreScroll(); // content is in the DOM — put the user back where they were

  // Hearing a level's dialogue through **in full** completes the whole topic —
  // whichever level it was — which is what its chapter's percentage is counted
  // from. Only the play bar's own completion signal gets here: a manual stop, a
  // level switch or leaving the screen all cancel it, the per-sentence buttons
  // never complete a run (so a single sentence can't finish a topic), and
  // `fromStart` rules out a run resumed mid-level.
  const doneBadge = headerEl.querySelector('#topic-done');
  const markDone = async () => {
    try {
      await markTopicDone(topicId);
    } catch {
      /* IndexedDB unavailable — nothing was persisted, so show nothing */
      return;
    }
    if (doneBadge) doneBadge.hidden = false;
  };

  // The bottom bar owns playback: the main button, the current-sentence highlight,
  // the autoscroll and the resume point (see `components/playBar.js`).
  const playback = createPlayBar(main, {
    list: levelEl,
    playlist: buildPlaylist,
    label: 'Odtwórz dialog',
    onComplete: ({ fromStart }) => {
      if (fromStart) markDone();
    },
  });

  // Level pills — switch level; that ends playback and drops the resume point
  // with it, since the new level has entirely different sentences. The switch has
  // to land **before** `reset()`: the controller re-reads `buildPlaylist()` right
  // there, and that reads `active` — resetting first would just re-cache the level
  // being replaced (and then play it).
  const onLevelClick = (event) => {
    const btn = event.target.closest('[data-tab]');
    if (!btn) return;
    const index = Number(btn.dataset.tab);
    if (!Number.isInteger(index) || index === active) return;
    active = index;
    renderActive();
    playback.reset();
  };
  levelStartEl.addEventListener('click', onLevelClick);
  levelEndEl.addEventListener('click', onLevelClick);

  /**
   * Flip one accordion row of `kind` ('sentence' | 'exercise'): the trigger's
   * `aria-expanded`, its panel's visibility, and — where there is one — the leading
   * chevron's rotation. Rows toggle independently, so several answers can be open
   * at once.
   */
  const toggleAccordion = (kind, id) => {
    const trigger = levelEl.querySelector(`[data-${kind}-toggle="${CSS.escape(id)}"]`);
    const panel = levelEl.querySelector(`[data-${kind}-panel="${CSS.escape(id)}"]`);
    if (!trigger || !panel) return;
    const open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;
    trigger.querySelector(`[data-${kind}-chevron]`)?.classList.toggle('rotate-90', !open);
  };

  /**
   * The four things a tap inside the level can mean, in priority order. Every
   * trigger is a real `<button>`, so Enter/Space arrive here as clicks and this
   * screen needs no keyboard handler of its own.
   */
  const actions = [
    // A played sentence becomes the resume point, so the main button carries on
    // from here — and being a single item, it can't complete the topic.
    [
      '[data-play-sentence]',
      (el) => playback.playOne(el.dataset.playSentence, audioBlob[el.dataset.playSentence]),
    ],
    // Same, except a phrase isn't in the playlist, so it leaves the dialogue's
    // resume point untouched.
    [
      '[data-play-phrase]',
      (el) => playback.playOne(el.dataset.playPhrase, phraseAudio[el.dataset.playPhrase]),
    ],
    // Phrase text → the standalone preview panel (reads the `phrases` store
    // itself; nothing from this screen is passed in but the id).
    ['[data-phrase-open]', (el) => openPhrasePreview(el.dataset.phraseOpen)],
    // Exercise prompt → reveal its English translation. Nothing to play here.
    ['[data-exercise-toggle]', (el) => toggleAccordion('exercise', el.dataset.exerciseToggle)],
    ['[data-sentence-toggle]', (el) => toggleAccordion('sentence', el.dataset.sentenceToggle)],
  ];

  levelEl.addEventListener('click', (event) => {
    for (const [selector, run] of actions) {
      const el = event.target.closest(selector);
      if (el) return run(el);
    }
  });
}

/** Topic detail screen — reached by tapping a topic row in a chapter. */
export function renderTopic(mount, topicId) {
  const content = `
    <div class="flex flex-1 flex-col py-6 pb-28">
      <header id="topic-header"></header>
      <section id="topic-level" class="mt-6"></section>
    </div>

    <!-- Bottom bar: level pill (left) + play control (centered) + level pill (right). -->
    ${playBarHtml({
      label: 'Odtwórz dialog',
      startId: 'topic-levels-start',
      endId: 'topic-levels-end',
    })}`;

  renderAppScreen(mount, {
    active: '',
    contentHtml: content,
    leading: backButton(),
    onMount(main) {
      loadTopicView(main, topicId);
    },
  });
}
